import React, { useMemo, useCallback } from 'react';
import { prop, pick } from 'js/utils/ramda.util';
import { Icon } from 'js/components/common/Icon';
import { useDeviceSelector } from 'js/store/hooks';
import { Loader } from 'js/components/common/Loader';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { useTransactions } from './useTransactions';
import { useDynamicReducerSelector } from '../../store';

import style from '../../DetailedTransactionHistory.scss';

export const useTableData = ({ isMobileView }) => {
    const translate = useComponentTranslations();
    const isMobile = useDeviceSelector(prop('isMobile'));
    const transactions = useTransactions();
    const { isHistoryLoading, page: { isPageChanged } } = useDynamicReducerSelector(pick([
        'isHistoryLoading', 'page'
    ]));
    const { showProductForTransactions, showFeesForTransactions } = useComponentConfig();

    const getHeaderCells = useCallback((transaction) => {
        const { type, status, time, methodName, accountId, amount } = transaction;

        return isMobile || isMobileView
            ? [{
                id: 0,
                className: style.type,
                content: (
                    <div className={style.highlight}>
                        <Icon type={type} className={style.icon} />
                        {translate(`transaction.type.${type}`)}
                    </div>
                )
            }, {
                id: 1,
                content: <><span className={style.amount}>{amount}</span><span>{time}</span></>,
                className: 'accordionHeaderSmallDetails'
            }]
            : [{
                id: 0,
                className: style.wideColumn,
                content: (
                    <div className={style.highlight}>
                        <Icon type={type} className={style.icon} />
                        {translate(`transaction.type.${type}`)}
                    </div>
                )
            }, {
                id: 1,
                content: translate(`transaction.status.${status}`),
                className: `${style[status]} ${style.narrowColumn}`
            },
                { id: 2, content: time },
                { id: 3, content: methodName },
                { id: 4, content: accountId },
                { id: 5, content: amount, className: `${style.amount} ${style.narrowColumn}` }];
    }, [isMobile, isMobileView, translate]);

    const getAccordionCell = useCallback((iconType, name, value) => (
        <>
            <Icon type={iconType} className={style.icon} />
            <div className="accordionBodyCellName">{translate(name)}</div>
            <div className="accordionBodyCellValue">{value}</div>
        </>
    ), [translate]);

    const getRowsCells = useCallback((transaction) => {
        const { clientType, status, fee, methodName, accountId, localTransactionId } = transaction;
        const mobileRows = [{
            id: 0,
            content: getAccordionCell('status', 'column.title.status', translate(`transaction.status.${status}`))
        }, {
            id: 1,
            content: getAccordionCell('mobileMethod', 'column.title.method', methodName)
        }, {
            id: 2,
            content: getAccordionCell('account', 'column.title.account', accountId)
        }];
        const commonRows = [
            localTransactionId && {
                id: 3, content: getAccordionCell('transactionId', 'transaction.id', localTransactionId)
            },
            showProductForTransactions && clientType && {
                id: 4,
                content: getAccordionCell('product', 'transaction.product', clientType)
            },
            showFeesForTransactions && fee && {
                id: 5,
                content: getAccordionCell('fee', 'transaction.fee', fee)
            }
        ].filter(Boolean);

        return isMobile || isMobileView ? [...mobileRows, ...commonRows] : commonRows;
    }, [isMobile, isMobileView, translate, showFeesForTransactions, showProductForTransactions, getAccordionCell]);

    return useMemo(() => {
        const rows = isHistoryLoading && !isPageChanged
            ? [{
                id: 0,
                headerCells: [{ id: 0, content: <Loader />, className: style.loader }]
            }]
            : transactions.map(transaction => ({
                id: transaction.localTransactionId,
                rowCells: getRowsCells(transaction),
                headerCells: getHeaderCells(transaction)
            }));
        const tableViewHeaderCells = !(isMobile || isMobileView) && transactions.length ? [
            { id: 0, content: translate('column.title.name'), className: style.wideColumn },
            { id: 1, content: translate('column.title.status'), className: style.narrowColumn },
            { id: 2, content: translate('column.title.date') },
            { id: 3, content: translate('column.title.method') },
            { id: 4, content: translate('column.title.account') },
            { id: 5, content: translate('column.title.amount'), className: style.narrowColumn }
        ] : [];

        return {
            accordionView: { rows, tableViewHeaderCells }
        };
    }, [
        isHistoryLoading, transactions, getHeaderCells, getRowsCells,
        isPageChanged, isMobile, isMobileView, translate
    ]);
};
